<script>
import Vue from "vue";
import { query, DEFAULT_TRAIT_GROUP, DEFAULT_MODEL } from "./utils/cvdiBioIndexUtils";
import Formatters from "@/utils/formatters";
import DataDownload from "@/components/DataDownload.vue";
import keyParams from "@/utils/keyParams";
import CVDIPigeanTable from "./CVDIPigeanTable.vue";
import uiUtils from "@/utils/uiUtils";
import alertUtils from "@/utils/alertUtils";
import plotUtils from "@/utils/plotUtils";
import sortUtils from "@/utils/sortUtils";
import dataConvert from "@/utils/dataConvert";
export default Vue.component("cvdi-pigean-table", {
    components: {
        DataDownload,
        CVDIPigeanTable,
    },
    props: [
        "pigeanData",
        "phenotypeMap",
        "config",
        "isSubtable",
        "filter",
    ],
    data() {
        return {
            perPage: 10,
            currentPage: 1,
            subtableData: {},
            plotColors: plotUtils.plotColors(),
        };
    },
    computed: {
        utilsBox() {
            let utils = {
                Formatters: Formatters,
                uiUtils: uiUtils,
                alertUtils: alertUtils,
                keyParams: keyParams,
                dataConvert: dataConvert,
                sortUtils: sortUtils,
                plotUtils: plotUtils,
            };
            return utils;
        },
        probFields() {
            return this.collateFields();
        },
        probData() {
            return this.computeProbabilities();
        },
        rows() {
            return this.pigeanData.length || 0;
        },
        sortBy() {
            return this.pigeanData.length === 0
                ? 0
                : this.config.fields
                      .map((field) => field.key)
                      .includes("factor_value")
                ? "factor_value"
                : this.config.sortBy
                ? this.config.sortBy
                : this.pigeanData[0]["combined"] !== undefined
                ? "combined"
                : "beta_uncorrected";
        },
        tableData() {
            let data = this.probData;
            if (this.filter) {
                data = data.filter(this.filter);
            }
            return data;
        },
        traitGroup() {
            return keyParams.traitGroup || DEFAULT_TRAIT_GROUP;
        },
        suffix() {
            return `&traitGroup=${this.traitGroup}`;
        },
    },
    methods: {
        phenotypeFormatter: Formatters.phenotypeFormatter,
        pValueFormatter: Formatters.pValueFormatter,
        effectFormatter: Formatters.effectFormatter,
        intFormatter: Formatters.intFormatter,
        annotationFormatter: Formatters.annotationFormatter,
        tissueFormatter: Formatters.tissueFormatter,
        tpmFormatter: Formatters.tpmFormatter,
        async getSubtable(row) {
            let queryKey = this.subtableKey(row.item);
            if (!this.subtableData[queryKey]) {
                let data = await query(this.config.subtableEndpoint, queryKey);
                if (this.config.subtableEndpoint === 'pigean-joined-gene-set'){
                    // De-duplicate trait groups on joined geneset endpoint.
                    data = data.filter(d => d.trait_group === row.item.trait_group);
                }
                Vue.set(this.subtableData, queryKey, data);
            }
        },
        showDetails(row) {
            row.toggleDetails();
            this.getSubtable(row);
        },
        subtableKey(item) {
            return `${item.phenotype},${item[this.config.queryParam]},${DEFAULT_MODEL}`;
        },
        generateId(label) {
            return label.replaceAll(",", "").replaceAll(" ", "_");
        },
        probability(val, prior = 0.05) {
            let a = Math.exp(Math.log(prior) + val);
            return a / (1 + a);
        },
        computeProbabilities() {
            let data = this.isSubtable
                ? this.pigeanData
                : this.describePhenotypes(this.pigeanData);
            for (let i = 0; i < this.config.fields.length; i++) {
                let fieldConfig = this.config.fields[i];
                if (!fieldConfig.showProbability) {
                    continue;
                }
                let field = fieldConfig.key;
                for (let j = 0; j < data.length; j++) {
                    if (!!data[j][field]) {
                        data[j][`${field}_probability`] = this.tpmFormatter(
                            this.probability(data[j][field])
                        );
                    }
                }
            }
            return data;
        },
        collateFields() {
            let allFields = [];
            this.config.fields.forEach((field) => {
                if (field.showProbability) {
                    allFields.push({
                        key: `${field.key}_probability`,
                        sortable: true,
                    });
                }
                allFields.push(field);
            });
            return allFields;
        },
        describePhenotypes(data) {
            let inputData = structuredClone(data);
            for (let i = 0; i < inputData.length; i++) {
                if (!inputData[i].phenotype) {
                    continue;
                }
                let desc = this.phenotypeMap[inputData[i].phenotype];
                if (desc === undefined) {
                    desc = { phenotype_name: inputData[i].phenotype };
                }
                let phenotypeDesc = desc.phenotype_name.trim();
                inputData[i]["phenotypeDesc"] = phenotypeDesc;
            }
            return inputData;
        },
    },
});
</script>
<template>
    <div id="pigean-gene" :class="isSubtable ? 'pigean-subtable' : ''">
        <div v-if="tableData.length > 0">
            <div v-if="!isSubtable" class="text-right mb-2">
                <data-download
                    :data="probData"
                    filename="pigean_gene"
                ></data-download>
            </div>
            <div v-html="'Total rows: ' + rows" class="table-total-rows"></div>
            <b-table
                :hover="isSubtable"
                small
                responsive
                :items="tableData"
                :fields="probFields"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by="sortBy"
                :sort-desc="true"
                :sort-null-last="true"
            >
                <template #cell(label)="r">
                    <span v-if="!!r.item.label">
                        {{
                            r.item.label.length > 50
                                ? `${r.item.label.slice(0, 50)}...`
                                : r.item.label
                        }}
                    </span>
                </template>
                <template #cell(gene)="r">
                    <a :href="`/cvdi_pigean/gene.html?gene=${r.item.gene}${suffix}`">
                        {{ r.item.gene }}
                    </a>
                </template>
                <template #cell(phenotypeDesc)="r">
                    <a
                        v-if="!!phenotypeMap[r.item.phenotype]"
                        :href="`/cvdi_pigean/phenotype.html?phenotype=${r.item.phenotype}${suffix}`"
                    >
                        {{ r.item.phenotypeDesc }}
                    </a>
                    <span v-else>{{ r.item.phenotype }}</span>
                </template>
                <template #cell(gene_set)="r">
                    <a
                        :href="`/cvdi_pigean/geneset.html?geneset=${r.item.gene_set}${suffix}`"
                    >
                        {{ r.item.gene_set }}
                    </a>
                </template>
                <template #cell(top_gene_sets)="r">
                    <div class="wrapped-cell-content">
                        <span v-if="r.item.top_gene_sets">
                            {{ r.item.top_gene_sets }}
                        </span>
                        <span v-else>-</span>
                    </div>
                </template>
                <template #cell(top_genes)="r">
                    <div class="wrapped-cell-content">
                        <span v-if="r.item.top_genes">
                            {{ r.item.top_genes }}
                        </span>
                        <span v-else>-</span>
                    </div>
                </template>
                <template #cell(expand)="row">
                    <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="showDetails(row)"
                    >
                        {{
                            row.detailsShowing
                                ? "Hide"
                                : "Show"
                        }}
                    </b-button>
                </template>
                <template #row-details="row">
                    <cvdi-pigean-table
                        v-if="
                            subtableData[subtableKey(row.item)] &&
                            subtableData[subtableKey(row.item)].length > 0
                        "
                        :pigeanData="subtableData[subtableKey(row.item)]"
                        :config="{ fields: config.subtableFields }"
                        :isSubtable="true"
                    >
                    </cvdi-pigean-table>
                </template>
            </b-table>
            <b-pagination
                v-model="currentPage"
                class="pagination-sm justify-content-center"
                :total-rows="tableData.length"
                :per-page="perPage"
            ></b-pagination>
        </div>
        <div v-else>
            <b-alert show variant="warning" class="text-center">
                <b-icon icon="exclamation-triangle"></b-icon> No data available
                for this query.
            </b-alert>
        </div>
    </div>
</template>
<style scoped>
@import url("/css/effectorGenes.css");

label {
    margin: 10px;
}
.pigean-subtable {
    font-size: smaller;
    margin-left: 15px;
    background-color: #efefef;
}
.pigean-subtable .row .col-12 {
    padding: 0 0 0 5px !important;
}
ul.top-list {
    font-size: 0.8rem;
}
.wrapped-cell-content {
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;
    max-width: 300px;
}
</style>
